import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import UserPopup from "./UserPopup";
import { FaEllipsisV, FaSearch, FaDownload, FaSyncAlt } from "react-icons/fa";
import { authHeaders } from "../utils/authHeaders";
import "./UserTable.css";

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

export default function UserTable() {
  const [users, setUsers] = useState([]);
  const [popupUser, setPopupUser] = useState(null);
  const [popupMode, setPopupMode] = useState("view");
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/user`,
          { headers: authHeaders(getStoredUser()) }
        );
        if (response.data.success) {
          setUsers(response.data.users);
        } else {
          alert("Gagal mengambil data users");
        }
      } catch (error) {
        console.error("Fetch users error:", error);
        alert("Terjadi kesalahan saat mengambil data users");
      }
    };

    fetchUsers();
  }, []);

  const handleView = (user) => {
    setPopupUser(user);
    setPopupMode("view");
    setDropdownOpen(null);
  };

  const handleEdit = (user) => {
    setPopupUser(user);
    setPopupMode("edit");
    setDropdownOpen(null);
  };

  const handleDelete = async (user) => {
    if (!user?.id) return;
    const ok = window.confirm(`Hapus user "${user.full_name}"?`);
    if (!ok) return;
    try {
      const res = await axios.delete(`${process.env.REACT_APP_API_URL}/user/${user.id}`, {
        headers: authHeaders(getStoredUser()),
      });
      if (res?.data?.success) {
        setUsers((prev) => prev.filter((u) => u.id !== user.id));
        setDropdownOpen(null);
      } else {
        alert(res?.data?.message || "Gagal menghapus user.");
      }
    } catch (err) {
      alert(err?.response?.data?.message || "Gagal menghapus user.");
    }
  };

  const handleExport = () => {
    const data = filteredUsers.map((u) => ({
      ID: u.id,
      Nama: u.full_name,
      Email: u.email,
      "No HP": u.phone,
      Role: u.role,
      Gender: u.gender,
      "Tanggal Lahir": u.birth_date?.split("T")[0] || "",
      "Alamat Lengkap": u.full_address || "",
      Kecamatan: u.kecamatan,
      Kabupaten: u.kabupaten,
      Provinsi: u.provinsi,
      "Jumlah Kebun": u.jumlah_kebun,
      "Luas Kebun (ha)": u.luas_kebun,
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "UserList");
    XLSX.writeFile(workbook, "user_list.xlsx");
  };

  const filteredUsers = users.filter((u) => {
    const keyword = searchTerm.toLowerCase();
    const matchesSearch =
      u.full_name?.toLowerCase().includes(keyword) ||
      u.phone?.toLowerCase().includes(keyword);
    const matchesRole = roleFilter ? u.role === roleFilter : true;
    return matchesSearch && matchesRole;
  });

  const clearFilters = () => {
    setSearchTerm("");
    setRoleFilter("");
  };

  return (
    <div className="user-table-container">
      <div className="user-table-toolbar">
        <div className="search-box">
          <FaSearch className="icon" />
          <input
            type="text"
            placeholder="Cari nama atau no HP..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">Semua Role</option>
          <option value="user">User</option>
          <option value="mitra sahabat kakao">Mitra Sahabat Kakao</option>
          <option value="sahabat kakao">Sahabat Kakao</option>
          <option value="field team">Field Team</option>
          <option value="management">Management</option>
          <option value="admin">Admin</option>
        </select>

        <div className="toolbar-actions">
          <button className="toolbar-button" onClick={clearFilters}>
            <FaSyncAlt /> Reset
          </button>

          <button className="toolbar-button export-btn" onClick={handleExport}>
            <FaDownload /> Export Excel
          </button>
        </div>
      </div>

      <div className="table-scroll-wrapper">
        <table className="user-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nama</th>
              <th>Email</th>
              <th>No HP</th>
              <th>Role</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u) => (
              <tr key={u.id}>
                <td>{u.id}</td>
                <td>{u.full_name}</td>
                <td>{u.email}</td>
                <td>{u.phone}</td>
                <td>{u.role}</td>
                <td>
                  <div className="aksi-dropdown-container">
                    <button
                      className="aksi-button"
                      onClick={() =>
                        setDropdownOpen(dropdownOpen === u.id ? null : u.id)
                      }
                    >
                      <FaEllipsisV />
                    </button>
                    {dropdownOpen === u.id && (
                      <div className="aksi-dropdown">
                        <div onClick={() => handleView(u)}>Lihat Profil</div>
                        <div onClick={() => handleEdit(u)}>Edit Data</div>
                        <div className="aksi-danger" onClick={() => handleDelete(u)}>Hapus User</div>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filteredUsers.length === 0 && (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", color: "#999" }}>
                  Tidak ada user ditemukan.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {popupUser && (
        <UserPopup
          user={popupUser}
          mode={popupMode}
          onClose={() => setPopupUser(null)}
          onSave={(updatedUser) => {
            setUsers((prev) =>
              prev.map((usr) =>
                usr.id === updatedUser.id ? updatedUser : usr
              )
            );
            setPopupUser(null);
          }}
        />
      )}
    </div>
  );
}
